import type { ComponentType, CSSProperties, HTMLAttributes, SVGProps } from "react";

/**
 * ICON-SWAP-1 — SF Symbol art, inlined at build time.
 *
 * Every file in src/assets/sf-icons/ is a vector export from Apple's SF
 * Symbols app (dragged out, decoded from the resulting .textClipping
 * files, and normalized: SF Symbols bakes a hardcoded `fill="white"` into
 * every path, which we swap for `currentColor` so these icons pick up
 * color exactly the way the lucide icons they replace already do, via a
 * Tailwind text-color class on the wrapper).
 *
 * `?raw` + `eager: true` is a native Vite feature — no bundler plugin
 * required. Deliberate, given this repo's history with dependencies that
 * are listed in package.json but never actually installed (see HOTFIX-1
 * in vite.config.ts): this needs nothing beyond what's already here.
 */
const rawIcons = import.meta.glob("../../assets/sf-icons/*.svg", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

const ICONS: Record<string, string> = {};
for (const path in rawIcons) {
  const name = path.slice(path.lastIndexOf("/") + 1).replace(/\.svg$/, "");
  ICONS[name] = rawIcons[path];
}

export interface OrbitIconProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  /** SF Symbol name, e.g. "camera.fill" — matches a file in src/assets/sf-icons/. */
  name: string;
  /**
   * Lucide component to render until this symbol has been exported and
   * dropped into src/assets/sf-icons/. Once the file exists, this prop
   * is simply never used — no follow-up code change needed.
   */
  fallback?: ComponentType<
    SVGProps<SVGSVGElement> & { size?: number | string; strokeWidth?: number | string }
  >;
  /** Pixel size shorthand, same convention as lucide's own `size` prop. */
  size?: number | string;
  /**
   * Kept for drop-in compatibility with existing lucide call sites. SF
   * Symbol art has no runtime stroke-weight lever — weight is baked into
   * which variant got exported — so this only does anything while a
   * symbol is still rendering through `fallback` (a real, stroke-based
   * lucide icon that does honor it).
   */
  strokeWidth?: number | string;
}

/**
 * Renders SF Symbol vector art. Falls back to a lucide icon automatically
 * when a symbol hasn't been exported yet, so call sites can adopt this
 * component ahead of full icon coverage with zero visual regression.
 */
export function OrbitIcon({
  name,
  fallback: Fallback,
  size,
  strokeWidth,
  className,
  style,
  ...rest
}: OrbitIconProps) {
  const svg = ICONS[name];

  if (!svg) {
    if (Fallback) {
      return <Fallback className={className} style={style} size={size} strokeWidth={strokeWidth} />;
    }
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[OrbitIcon] no artwork for "${name}" and no fallback was given.`);
    }
    return null;
  }

  const sizeStyle: CSSProperties = size !== undefined ? { width: size, height: size } : {};

  return (
    <span
      className={className}
      style={{ ...sizeStyle, ...style }}
      data-orbit-icon={name}
      dangerouslySetInnerHTML={{ __html: svg }}
      {...rest}
    />
  );
}
